import { InjectionToken } from '@angular/core';
import type { ManageScene, PhotoSortMode } from '../entities/gallery-manage.models';

// Delete refuses when a scene photo sits in an approved (locked) list — the
// server answers 409 with errorCode 'SCENE_LOCKED' and details.lockedCount. The
// code alone means locked; the port surfaces the count (0 when absent) so the UI
// can explain the refusal instead of a bare failure.
export type DeleteSceneResult = { ok: true } | { ok: false; locked?: number };

// Add-video: the server rejects an unparseable/unsupported provider URL — the
// port distinguishes it so the dialog can show a precise message rather than a
// bare failure. `file_still_deleting`: an uploaded file is refused while still queued for deletion.
export type AddVideoResult =
  | { ok: true; sceneId: string }
  | { ok: false; reason: 'invalid_url' | 'file_still_deleting' | 'unknown' };

// Update carries the video id back: replacing the link retires the old row, so the
// poster of the same save has to land on the row the server actually kept.
export type UpdateVideoResult = { ok: true; videoId: string } | { ok: false; reason: 'invalid_url' | 'unknown' };

// Mirrors the gallery-service PhotoDeleteResult workflow envelope: locked (an
// approved-list member) and cover_warning (would delete the cover) are outcomes
// the UI branches on, not errors. 'error' is the client-side transport failure.
export type PhotoDeleteResult =
  | { ok: true }
  | { ok: false; reason: 'not_found' | 'no_access' | 'error' }
  | { ok: false; reason: 'locked'; lockedCount: number }
  | { ok: false; reason: 'cover_warning'; coverPhotoId: string };

// Чтение сцен различает ровно два провала: 403/404 = галерея не наша (или её нет) —
// экран «Галерея недоступна»; всё остальное — сбой связи с кнопкой повтора. Без этого
// деления упавший Wi-Fi врал бы фотографу, что галерея чужая.
export type ScenesResult = { ok: true; scenes: ManageScene[] } | { ok: false; reason: 'no_access' | 'error' };

export interface GalleryManageDataPort {
  scenes(galleryId: string): Promise<ScenesResult>;
  movePhotos(
    galleryId: string,
    photoIds: readonly string[],
    targetSceneId: string,
    targetOrderedIds: readonly string[],
    toIndex: number,
  ): Promise<boolean>;
  movePhotosToGallery(galleryId: string, photoIds: readonly string[], targetGalleryId: string): Promise<boolean>;
  deletePhotos(galleryId: string, photoIds: readonly string[], confirmCover?: boolean): Promise<PhotoDeleteResult>;
  // Повторная обработка одного фото после отказа конвейера («Повторить» на плитке).
  reprocessPhoto(photoId: string): Promise<boolean>;
  reorderScenes(galleryId: string, orderedIds: readonly string[]): Promise<boolean>;
  setVideoPoster(videoId: string, photoId: string | null): Promise<boolean>;
  addScene(galleryId: string, name?: string): Promise<string | null>;
  // Embed a YouTube/Vimeo video: converts the given empty photo scene in place, or
  // appends a new video scene when it is not empty. sceneId is any existing scene.
  addVideo(galleryId: string, sceneId: string, url: string, name?: string): Promise<AddVideoResult>;
  // A directly-uploaded native video: same anchor rule, but the source is the already-
  // uploaded file's public URL (provider='upload'), not an embed link.
  addVideoFile(galleryId: string, sceneId: string, fileUrl: string, fileId?: string, name?: string): Promise<AddVideoResult>;
  // «Редактировать видео»: the link and the scene name change in ONE server operation.
  updateVideo(sceneId: string, url: string, name: string): Promise<UpdateVideoResult>;
  renameScene(sceneId: string, name: string): Promise<boolean>;
  // Client-only visibility: a hidden scene is served to the client tier only,
  // guests never receive it (spec 04 «Client-only photo sets»).
  setSceneHidden(sceneId: string, isHidden: boolean): Promise<boolean>;
  // Один режим на всю галерею: сервер проставляет его каждой сцене одним запросом.
  setGallerySortMode(galleryId: string, sortMode: PhotoSortMode): Promise<boolean>;
  deleteScene(sceneId: string): Promise<DeleteSceneResult>;
  setSceneDescription(sceneId: string, description: string): Promise<boolean>;
  clearSceneDescription(sceneId: string): Promise<boolean>;
}

export const GALLERY_MANAGE_DATA = new InjectionToken<GalleryManageDataPort>('GALLERY_MANAGE_DATA');

const MOCK_SCENES: ManageScene[] = [
  {
    id: 'scene-1',
    name: 'Фотографии',
    kind: 'photo',
    description: null,
    isHidden: false,
    photoSortMode: 'manual',
    sortOrder: 0,
    photoCount: 3,
    videoCount: 0,
    photos: [
      { id: 'p1', thumbnailUrl: null, isClientPrivate: false, sortOrder: 0, originalName: null, processingStatus: 'ready' },
      { id: 'p2', thumbnailUrl: null, isClientPrivate: false, sortOrder: 1, originalName: null, processingStatus: 'ready' },
      { id: 'p3', thumbnailUrl: null, isClientPrivate: true, sortOrder: 2, originalName: null, processingStatus: 'ready' },
    ],
    videos: [],
  },
  {
    id: 'scene-2',
    name: 'Прогулка',
    kind: 'photo',
    description: 'Вторая сцена с описанием.',
    isHidden: true,
    photoSortMode: 'manual',
    sortOrder: 1,
    photoCount: 0,
    videoCount: 0,
    photos: [],
    videos: [],
  },
];

export class MockGalleryManageData implements GalleryManageDataPort {
  async scenes(galleryId: string): Promise<ScenesResult> {
    return galleryId ? { ok: true, scenes: structuredClone(MOCK_SCENES) } : { ok: false, reason: 'error' };
  }

  async movePhotos(): Promise<boolean> {
    return true;
  }

  async movePhotosToGallery(): Promise<boolean> {
    return true;
  }

  async deletePhotos(): Promise<PhotoDeleteResult> {
    return { ok: true };
  }

  async reprocessPhoto(): Promise<boolean> {
    return true;
  }

  async reorderScenes(): Promise<boolean> {
    return true;
  }

  async setVideoPoster(): Promise<boolean> {
    return true;
  }

  async addScene(): Promise<string | null> {
    return 'scene-new';
  }

  async addVideo(): Promise<AddVideoResult> {
    return { ok: true, sceneId: 'scene-video' };
  }

  async addVideoFile(): Promise<AddVideoResult> {
    return { ok: true, sceneId: 'scene-video-file' };
  }

  async updateVideo(): Promise<UpdateVideoResult> {
    return { ok: true, videoId: 'video-updated' };
  }

  async renameScene(): Promise<boolean> {
    return true;
  }

  async setSceneHidden(): Promise<boolean> {
    return true;
  }

  async setGallerySortMode(): Promise<boolean> {
    return true;
  }

  async deleteScene(): Promise<DeleteSceneResult> {
    return { ok: true };
  }

  async setSceneDescription(): Promise<boolean> {
    return true;
  }

  async clearSceneDescription(): Promise<boolean> {
    return true;
  }
}
